import { Box, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { PieChart } from 'components/commons';
import { SumChart } from 'components/features/Dashboard';
import { useClusterDbSocketContext } from 'components/hooks/useClusterDbSocketContext';
import { useHeaderContext } from 'components/providers';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { DatePattern, getKeyFromLocalStorage } from 'utils/constant';
import { getVisits } from 'utils/fetch';
import ja from 'utils/ja';

interface DataPieChartType {
  id: number;
  value: number;
  label: string;
}

const getDatesBetween = (startDate?: string, endDate?: string) => {
  if (!startDate || !endDate) {
    return [];
  }

  const dates = [];
  let currentDate = dayjs(startDate).startOf('day');

  while (currentDate.isBefore(dayjs(endDate).add(1, 'day'), 'day')) {
    dates.push(currentDate.format('MM/DD'));
    currentDate = currentDate.add(1, 'day');
  }

  return dates;
};

const analyzeDataVisit = (
  data: IReferenceVisit[],
  startDate?: string,
  endDate?: string
) => {
  let visitCount = 0;
  let maleCount = 0;
  let femaleCount = 0;
  const ageCount = [0, 0, 0, 0, 0, 0, 0];
  const totalPerson = data.length;
  const rangeDay = getDatesBetween(startDate, endDate);
  const dataByRangeDay = [0, 0, 0, 0, 0, 0, 0];

  if (!totalPerson) {
    return {
      visitCount,
      dataByRangeDay: [],
      maleCount,
      femaleCount,
      ageCount,
    };
  }

  data.forEach((person) => {
    visitCount += person.visits.length;

    if (person.gender === 'male') {
      maleCount++;
    } else if (person.gender === 'female') {
      femaleCount++;
    }

    if (person.age < 10) {
      ageCount[0]++;
    } else if (person.age < 20) {
      ageCount[1]++;
    } else if (person.age < 30) {
      ageCount[2]++;
    } else if (person.age < 40) {
      ageCount[3]++;
    } else if (person.age < 50) {
      ageCount[4]++;
    } else if (person.age < 60) {
      ageCount[5]++;
    } else {
      ageCount[6]++;
    }

    if (rangeDay.length) {
      let preDay = '';
      person.visits.forEach((visit: IVisits) => {
        const date = dayjs.utc(visit.endTime).local().format('MM/DD');
        const index = rangeDay.findIndex((day) => day === date);
        if (index >= 0 && preDay !== date) {
          dataByRangeDay[index]++;
        }
        preDay = date;
      });
    }
  });

  return {
    visitCount,
    maleCount: Math.round((maleCount / totalPerson) * 100),
    femaleCount: Math.round((femaleCount / totalPerson) * 100),
    ageCount: ageCount.map((age) => Math.round((age / totalPerson) * 100)),
    dataByRangeDay: Object.values(dataByRangeDay),
  };
};

const Dashboard = () => {
  const theme = useTheme();
  const { rangeDate } = useHeaderContext();
  const { eventId } = useClusterDbSocketContext();

  const [arrayRangeDay, setArrayRangeDay] = useState<string[]>([]);
  const [dataChart, setDataChart] = useState<{
    visitCount: number;
    unique: number[];
    gender: DataPieChartType[];
    age: DataPieChartType[];
  }>({
    visitCount: 0,
    unique: [],
    gender: [],
    age: [],
  });

  const handleGetReference = async () => {
    const start = dayjs(rangeDate.start).startOf('day');
    const end = dayjs(rangeDate.end).endOf('day');
    const search = {
      startTime: start.utc().format(DatePattern.UTC_DATE),
      endTime: end.utc().format(DatePattern.UTC_DATE),
    };

    try {
      const appId = getKeyFromLocalStorage('appId');
      const appKey = getKeyFromLocalStorage('appKey');
      const dbId = getKeyFromLocalStorage('dbId');
      const dbKey = getKeyFromLocalStorage('dbKey');

      const { data } = await getVisits(search, {
        appId: appId ?? '',
        appKey: appKey ?? '',
        dbId: dbId ?? '',
        dbKey: dbKey ?? '',
      });

      const resultChart = analyzeDataVisit(
        data,
        rangeDate.start,
        rangeDate.end
      );

      setDataChart({
        visitCount: resultChart.visitCount ?? 0,
        unique: resultChart.dataByRangeDay,
        gender:
          !resultChart.maleCount && !resultChart.femaleCount
            ? []
            : [
                {
                  id: 0,
                  value: resultChart.maleCount,
                  label: ja.common.male,
                },
                {
                  id: 1,
                  value: resultChart.femaleCount,
                  label: ja.common.female,
                },
              ],
        age: !resultChart.ageCount.every((num: number) => num === 0)
          ? [
              {
                id: 0,
                value: resultChart.ageCount[0],
                label: '子供',
              },
              {
                id: 1,
                value: resultChart.ageCount[1],
                label: '10代',
              },
              {
                id: 2,
                value: resultChart.ageCount[2],
                label: '20代',
              },
              {
                id: 3,
                value: resultChart.ageCount[3],
                label: '30代',
              },
              {
                id: 4,
                value: resultChart.ageCount[4],
                label: '40代',
              },
              {
                id: 5,
                value: resultChart.ageCount[5],
                label: '50代',
              },
              {
                id: 6,
                value: resultChart.ageCount[6],
                label: '60以上',
              },
            ]
          : [],
      });
    } catch (error) {
      // empty
    }
  };

  useEffect(() => {
    if (rangeDate) {
      setArrayRangeDay(getDatesBetween(rangeDate.start, rangeDate.end));
      handleGetReference();
    }
  }, [rangeDate, eventId]);

  return (
    <Box
      sx={{
        height: { xs: 'calc(100dvh - 60px)', sm: 'calc(100dvh - 80px)' },
        mt: { xs: '60px', sm: '80px' },
        backgroundColor: '#F8F9FA',
        padding: '10px',
        overflowY: 'auto',
      }}
    >
      <SumChart
        dataCount={[
          { title: ja.dashboard.visits, value: dataChart.visitCount },
        ]}
      />
      <Typography
        sx={{
          textAlign: 'center',
          fontWeight: '700',
          fontSize: '25px !important',
          my: '12px',
        }}
      >
        {ja.dashboard.unique_user}
      </Typography>
      <Box
        sx={{
          borderRadius: '8px',
          border: '0.5px solid #BABABA',
          boxshadow: '0px 2px 2px 0px #00000040',
          mt: '10px',
          boxSizing: 'border-box',
          minHeight: 300,
        }}
      >
        {arrayRangeDay.length && dataChart.unique.length ? (
          <BarChart
            xAxis={[{ scaleType: 'band', data: arrayRangeDay }]}
            series={[{ data: dataChart.unique }]}
            height={300}
          />
        ) : (
          <Typography
            component='p'
            sx={{ textAlign: 'center', lineHeight: '250px' }}
          >
            {ja.message_error.no_data}
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          textAlign: 'center',
          fontWeight: '700',
          fontSize: '25px !important',
          my: '12px',
        }}
      >
        {ja.dashboard.attribute_graph}
      </Typography>
      <Box
        sx={{
          borderRadius: '8px',
          border: '0.5px solid #BABABA',
          boxshadow: '0px 2px 2px 0px #00000040',
          mt: '10px',
        }}
      >
        <PieChart
          title={ja.dashboard.gender}
          colors={[theme.palette.primary.main, '#F564A9']}
          data={dataChart.gender}
          label={[
            { color: theme.palette.primary.main, label: ja.common.male },
            { color: '#F564A9', label: ja.common.female },
          ]}
        />
      </Box>
      <Box
        sx={{
          borderRadius: '8px',
          border: '0.5px solid #BABABA',
          boxshadow: '0px 2px 2px 0px #00000040',
          mt: '10px',
        }}
      >
        <PieChart
          title={ja.dashboard.age}
          colors={[
            '#7DC9F7',
            '#4BB0F4',
            '#2996F0',
            '#2180CC',
            '#1A69A6',
            '#135280',
            '#135280',
          ]}
          data={dataChart.age}
          label={[
            { color: '#7DC9F7', label: '子供' },
            { color: '#4BB0F4', label: '10代' },
            { color: '#2996F0', label: '20代' },
            { color: '#2180CC', label: '30代' },
            { color: '#1A69A6', label: '40代' },
            { color: '#135280', label: '50代' },
            { color: '#135280', label: '60以上' },
          ]}
        />
      </Box>
    </Box>
  );
};

export default Dashboard;
